import {
  Button,
  InfoDataTable,
  ModalComponentWrapper,
} from '@/shared/components';
import type { AccountOption, InternalTransferConfirmationArgs } from '../model';
import { useInternalTransferExchangeRate } from '../model';
import { useTranslation } from 'react-i18next';
import { useInternalTransfer } from '../api';
import { useMemo } from 'react';
import InternalTransferLeverageBanner from './InternalTransferLeverageBanner';
import { formatReceivedAmount } from '@/shared/lib';
import { NUMERIC_CONFIG } from '@/shared/config';

type InternalTransferConfirmationModalProps =
  InternalTransferConfirmationArgs & {
    openResultModal: (params: { success: boolean; error?: Error }) => void;
    onBack: () => void;
  };

export const InternalTransferConfirmationModal = ({
  formValues,
  selectedAccountFrom,
  selectedAccountTo,
  selectedAssetFrom,
  selectedAssetTo,
  onSuccess,
  openResultModal,
  onBack,
}: InternalTransferConfirmationModalProps) => {
  const { t } = useTranslation();

  const { mutate: submitTransfer, isTransferring } = useInternalTransfer({
    onSuccess: data => {
      openResultModal({ success: true });
      onSuccess?.(data);
    },
    onError: error => {
      openResultModal({ success: false, error });
    },
  });

  const formatAccount = (account?: AccountOption) => {
    if (!account) return;
    return `(${t(`internalTransferModal.${account.type}`)}${account.accName ? ` ${account.accName}` : ''}${account.tradingProductCode ? ` ${account.tradingProductCode}` : ''}${account?.leverage ? ` ${account?.leverage}x` : ''})`;
  };

  const { secondData } = useInternalTransferExchangeRate(
    selectedAssetFrom?.code ?? formValues.assetFrom,
    selectedAssetTo?.code ?? formValues.assetTo,
    Number(
      formValues.amount?.replaceAll(
        NUMERIC_CONFIG.decimal.display,
        NUMERIC_CONFIG.decimal.internal
      )
    )
  );

  const data = useMemo(() => {
    return [
      {
        key: t('internalTransferModal.from'),
        value: `${selectedAssetFrom?.code} ${formatAccount(selectedAccountFrom)}`,
      },
      {
        key: t('internalTransferModal.to'),
        value: `${selectedAssetTo?.code} ${formatAccount(selectedAccountTo)}`,
      },
      {
        key: t('common.amountPlaceholder'),
        value:
          `${formatReceivedAmount(formValues.amount?.replaceAll(NUMERIC_CONFIG.decimal.display, NUMERIC_CONFIG.decimal.internal))} ${selectedAssetFrom?.code ?? ''}`.trim(),
      },
    ];
  }, [
    formValues.accNumFrom,
    formValues.accNumTo,
    formValues.amount,
    selectedAccountFrom,
    selectedAccountTo,
    selectedAssetFrom,
    selectedAssetTo,
    t,
  ]);

  const title = useMemo(() => {
    return t(
      selectedAccountFrom?.type === 'leverage'
        ? 'internalTransferModal.confirmationModalTitle'
        : 'internalTransferModal.confirmationWithExchangeModalTitle'
    );
  }, [t, selectedAccountFrom, selectedAccountTo]);

  return (
    <ModalComponentWrapper title={title} onBack={onBack}>
      <div className="w-full flex flex-col gap-4">
        <InfoDataTable
          data={data}
          className="py-[1.6rem] px-[2.4rem] bg-secondary-bg rounded-[1.6rem]"
        />
        {secondData?.length > 0 && (
          <InfoDataTable
            data={secondData}
            className="py-[1.6rem] px-[2.4rem] bg-secondary-bg rounded-[1.6rem] mb-[2.4rem]"
          />
        )}
        {selectedAccountFrom?.type === 'leverage' && (
          <InternalTransferLeverageBanner />
        )}
      </div>
      <div className="w-full flex gap-[1.6rem] mb-[0.8rem]">
        <Button
          variant="default"
          loading={isTransferring}
          size="lg"
          onClick={() =>
            submitTransfer({
              ...formValues,
              amount: formValues.amount.toString(),
            })
          }
          className="flex-1">
          {t('common.submit')}
        </Button>
      </div>
      {selectedAssetFrom?.code != selectedAssetTo?.code && (
        <p className="text-center p4 text-secondary">
          {t('exchange.confirmationDescription')}
        </p>
      )}
    </ModalComponentWrapper>
  );
};
